import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { StyleSheet, css } from 'aphrodite';
import { parse, stringify } from 'query-string';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import './theme/tokens.css';

import { useWebsocketConnectionStatus } from '../voice-agent/hooks/useWebsocketConnectionStatus';

import { CountBadge } from '../../web/components-v2/CountBadge';
import {
	AppDrawerContent,
	AppDrawerToolbarButton,
	CoffeeDrawerProvider,
	CoffeeDrawerToolbar,
	SideDrawer,
	useAppDrawerContext,
} from '../../web/components-v2/Drawer/AppDrawer';
import { SettingsIcon } from '../../web/components/svgs/icons/SettingsIcon';
import { useLocalStorageState } from '../../hooks/useLocalStorage';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LeadServedSource } from '../viewModels/queue';
import {
	hasFiredBeforeCallPins,
	markBeforeCallPinsFired,
	useCoaching,
	useRecentCallsFeedback,
	waitForCoachingConversation,
} from './ai';
import { useCallPipeline } from './call';
import type { IContact, IPhoneNumber } from '../../extViewmodels';
import { toast } from 'sonner';
import {
	apiErrorMessage,
	queryKeys,
	useAddContact,
	useAttachCallToLead,
	useDeleteContact,
	useDialedNumbers,
	useFindCellPhone,
	useLead,
	useLeadContacts,
	useNextLead,
	usePhoneCallById,
	useSaveCallNote,
	useSkipLead,
	useSkipReasons,
	toUnreadCountByE164,
	useTextInbox,
	useToggleDecisionMaker,
	useUnreadTextConversations,
	useUpdateCompany,
	useUpdateContact,
	withContacts,
} from './data';
import type { FocusedLeadContact, FocusedLeadContactPhone, TextInboxItem } from './data';
import { CellPhonePickerDialog } from './contacts/CellPhonePickerDialog';
import { ContactDialog, type ContactDraft } from './contacts/ContactDialog';
import {
	invalidateMyCoachingAdherenceQueries,
	useMyCoachingAdherenceQuery,
} from '../../queries/AIConversation/useMyCoachingAdherenceQuery';
import { useMarkWrongNumberMutation } from '../queries/CellPhoneLookup';
import type { ICellPhoneLookupMatch, ICellPhoneLookupResult } from '../queries/CellPhoneLookup';
import { LOCKED_KEYWORD_PINS, matchKeywordIn, splitBeforeCallPins, useKeywordPins, usePinnedMessages } from './pins';
import type { KeywordPin, PinTrigger } from './pins';
import { PracticeModePanel } from './practice/PracticeModePanel';
import { usePracticeMode } from './practice/usePracticeMode';
import { useMyCategories, useMyScenarios, useMySkillAreas } from './spar/useScenarios';
import { useSparSession } from './spar/useSparSession';
import { SettingsPanel, useCoachingPreferences } from './settings';
import {
	createVoiceAgentSource,
	createVoiceAgentTranscriptBridge,
	twilioTranscriptSource,
	useTranscriptStream,
} from './transcript';
import type { TranscriptEntry, TranscriptSource } from './transcript';
import { AIChatPanel } from './ui/AIChatPanel';
import { PastCallsPanel } from './ui/PastCallsPanel';
import { RecentCallsFeedbackPanel } from './ui/RecentCallsFeedbackPanel';
import { CenterColumn } from './ui/CenterColumn';
import { CompanyPhonesDialog } from './ui/CompanyPhonesDialog';
import { ComposeEmailDialog } from './email/ComposeEmailDialog';
import {
	hasActiveDemo,
	numberOnlyPhone,
	resolveOpenTextTarget,
	TextConversationDialog,
	TextingBlockedDialog,
	useNewTextToasts,
	useRepTextingNumber,
} from './texting';
import { EditCompanyDialog } from './ui/EditCompanyDialog';
import { ReportIssueDialog } from './ui/ReportIssueDialog';
import { EmptyQueueState } from './ui/EmptyQueueState';
import { type DialerTabActivity, useDialerTabIndicator } from '../hooks/useDialerTabIndicator';
import { EnableAudioDialog } from './ui/EnableAudioDialog';
import {
	IconCalendarNote,
	IconFileText,
	IconMessageSquare,
	IconMic,
	IconPin,
	IconPlus,
	IconSearch,
	IconSparkles,
} from './ui/icons';
import { LeftColumn } from './ui/LeftColumn';
import { PinConfigPanel } from './ui/PinConfigPanel';
import { PinEditor } from './ui/PinEditor';
import { SearchPanel } from './search';
import { ColumnResizeHandle, DEFAULT_LEFT_COL_WIDTH, LEFT_COL_WIDTH_KEY } from './ui/ColumnResizeHandle';
import { FocusedNav } from './ui/FocusedNav';
import { TextInboxMenu } from './ui/TextInboxMenu';
import { TopBar } from './ui/TopBar';
import { PacingMilestoneDialog, useDialMilestoneTrigger, useDialPacing } from './pacing';
import { useFocusedViewEnabled, useFocusedViewLocked, usePinsLocked } from './useFocusedViewEnabled';
import { isRichTextEmpty, QuickActionsPanel, SkipReasonDialog, WrapupPanel } from './wrapup';
import type { ISkipReason } from '../viewModels/queue';

const DRAWER_VALUE_WRAPUP = 'focused-view-wrapup';
const DRAWER_VALUE_QUICK_ACTIONS = 'focused-view-quick-actions';
const DRAWER_VALUE_PIN_CONFIG = 'focused-view-pin-config';
const DRAWER_VALUE_SETTINGS = 'focused-view-settings';
const DRAWER_VALUE_SEARCH = 'focused-view-search';
const DRAWER_VALUE_AI = 'focused-view-ai';
const DRAWER_VALUE_FEEDBACK = 'focused-view-feedback';
const DRAWER_VALUE_PAST_CALLS = 'focused-view-past-calls';
const DRAWER_VALUE_PRACTICE = 'focused-view-practice';

type EditorState = null | 'new' | KeywordPin;

type SavePinPatch = {
	id?: string;
	label: string;
	keyword: string;
	color: string;
	prompt: string;
	trigger: PinTrigger;
	enabled: boolean;
	builtin?: boolean;
};

export const FocusedLeadView: FC = () => {
	const [drawerValue, setDrawerValue] = useState<string>('');

	return (
		<CoffeeDrawerProvider value={drawerValue} onValueChange={setDrawerValue}>
			<FocusedLeadViewContent />
		</CoffeeDrawerProvider>
	);
};

const FocusedLeadViewContent: FC = () => {
	const history = useHistory();
	const location = useLocation();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const pipeline = useCallPipeline();
	const appDrawerContext = useAppDrawerContext();
	const [leftColWidth, setLeftColWidth] = useLocalStorageState<number>(LEFT_COL_WIDTH_KEY, DEFAULT_LEFT_COL_WIDTH);
	const [showLiveTranscript, setShowLiveTranscript] = useLocalStorageState<boolean>(
		'focused-view:show-live-transcript',
		true
	);
	const { state: callState, muted, initDevice, accept, reject, hangup, setMuted, sendDigits } = pipeline;

	const isCoffeeAccount = userSession.account.isLevitateSalesCoffeeAccount;
	const [focusedViewEnabled, setFocusedViewEnabled] = useFocusedViewEnabled();
	const focusedViewLocked = useFocusedViewLocked();

	const coachingPrefs = useCoachingPreferences();

	const pinsLocked = usePinsLocked();
	const { pins: ownKeywordPins, create, update, remove, toggleEnabled } = useKeywordPins({ enabled: !pinsLocked });
	const keywordPins = pinsLocked ? LOCKED_KEYWORD_PINS : ownKeywordPins;
	const { pinnedIds, togglePin, pin: pinMessage, clear: clearPinned } = usePinnedMessages();

	const queryCompanyId = useMemo(() => {
		const parsed = parse(location.search);
		const value = parsed.companyId;
		return typeof value === 'string' && value.length > 0 ? value : undefined;
	}, [location.search]);

	// `?openText=<contactId>` arrives from unread-text notification links.
	const queryOpenTextContactId = useMemo(() => {
		const value = parse(location.search).openText;
		return typeof value === 'string' && value.length > 0 ? value : undefined;
	}, [location.search]);
	// `?openTextPhone=<e164>` comes from the in-app text inbox: the number that texted, which may not be the contact's first.
	const queryOpenTextPhone = useMemo(() => {
		const value = parse(location.search).openTextPhone;
		return typeof value === 'string' && value.length > 0 ? value : undefined;
	}, [location.search]);

	// Mirrors legacy `?from=<LeadServedSource>` attribution used by `SearchPanel`
	// (`?from=Search`), inbound-call landings (`?from=IncomingCall`), shared
	// links (`?from=SharedLink`), etc. Plumbed into `WrapupPanel.source` so
	// wrap-up saves are correctly attributed in analytics. Defaults to `Rule`
	// to preserve existing behaviour for queue-driven loads.
	const queryLeadServedSource = useMemo<LeadServedSource>(() => {
		const parsed = parse(location.search);
		const raw = parsed.from;
		if (typeof raw !== 'string' || raw.length === 0) {
			return LeadServedSource.Rule;
		}
		const validValues = Object.values(LeadServedSource) as string[];
		return validValues.includes(raw) ? (raw as LeadServedSource) : LeadServedSource.Rule;
	}, [location.search]);

	const [editing, setEditing] = useState<EditorState>(null);
	const [currentCompanyId, setCurrentCompanyId] = useState<string | undefined>(queryCompanyId);
	const [queueEmpty, setQueueEmpty] = useState(false);
	// Captures the phoneCallId from the most recently ended call so the Wrap-up
	// panel still has a valid id after `call:ended` (at which point the connected
	// state is gone). Populated via a ref-driven `call:connected` subscription
	// below so we don't have to depend on render ordering between events.
	const [lastEndedPhoneCallId, setLastEndedPhoneCallId] = useState<string | undefined>(undefined);

	// Twilio audio reconnect state. The pipeline emits `call:reconnecting` when
	// the SDK loses signaling on an active call and `call:reconnected` when it
	// recovers (or `call:ended` if it gives up). Surfaced to the rep as an
	// amber pill on the CallStrip — without it, audio drops are silent and the
	// rep just hears the line go dead.
	const [reconnecting, setReconnecting] = useState(false);

	// Mirrors legacy `useTelephony().isPendingOutcome`: latches `true` on
	// `call:ended` and clears once the wrap-up submits. Used to gate
	// Next-lead / Exit so the rep can't skip the call before logging an
	// outcome. The wrap-up state itself is owned by `WrapupPanel`; this
	// flag is the projection FocusedLeadView needs for its surrounding
	// gates (TopBar, exit button).
	const [isPendingOutcome, setIsPendingOutcome] = useState(false);
	const [hasDispositioned, setHasDispositioned] = useState(false);
	useEffect(() => {
		setHasDispositioned(false);
	}, [currentCompanyId]);

	// Skip-lead-without-calling state. The wrap-up panel has its own
	// SkipReasonDialog instance for the BadLead path (auto-opens after a
	// call ends). The TopBar entry-point is for the queue-without-calling
	// flow — rep advances past a lead they don't want to dial — so it lives
	// here and uses its own dialog/mutation pair.
	const [skipDialogOpen, setSkipDialogOpen] = useState(false);
	const [skipDialogMode, setSkipDialogMode] = useState<'skip' | 'remove' | undefined>();
	const [reportIssueOpen, setReportIssueOpen] = useState(false);
	const [pendingSkipReasonId, setPendingSkipReasonId] = useState<string | null>(null);
	// Caches a note already saved for the current skip attempt (keyed by its
	// HTML) so a skip-retry after a failure reuses it instead of creating a
	// duplicate. Cleared on success and when the dialog closes.
	const skipNoteCacheRef = useRef<{ html: string; noteId: string } | null>(null);
	const skipReasonsResult = useSkipReasons(skipDialogOpen ? currentCompanyId : undefined);
	const skipLeadMutation = useSkipLead();
	const saveSkipNoteMutation = useSaveCallNote();

	// CallFromUnknown rescue: when an inbound call connects with no companyId
	// on the phone-call record, auto-open the SearchPanel in rescue mode so
	// the rep can attach the call to a lead. Mirrors legacy `SearchModal`'s
	// `CallFromUnknown` flow. Cleared on call end or successful attach.
	const [unknownInboundCall, setUnknownInboundCall] = useState<{ phoneCallId: string; from: string } | null>(null);
	const inboundLookup = usePhoneCallById(unknownInboundCall?.phoneCallId);

	// Phone-call id + raw E.164 of the currently ringing inbound call,
	// captured on `call:incoming` so we can resolve the caller's identity
	// against our lead store while the strip is still ringing. Cleared on
	// connect / end so we don't keep stale data around.
	const [ringingInboundPhoneCallId, setRingingInboundPhoneCallId] = useState<string | undefined>(undefined);
	const [ringingInboundFrom, setRingingInboundFrom] = useState<string | undefined>(undefined);
	const ringingInboundLookup = usePhoneCallById(ringingInboundPhoneCallId);
	const ringingInboundCompanyId = ringingInboundLookup.data?.companyId;
	const ringingInboundBaseLead = useLead(ringingInboundCompanyId);
	const ringingInboundContacts = useLeadContacts(ringingInboundCompanyId);
	const ringingInboundDisplay = useMemo(() => {
		const company = ringingInboundBaseLead.data?.company?.trim();
		const contacts = ringingInboundContacts.data;
		const callerContactId = ringingInboundLookup.data?.contactIds?.[0];
		const from = ringingInboundFrom;

		// Resolve the specific caller: prefer the server-stamped match on the
		// phone-call record, then fall back to a manual E.164 match against
		// the lead's contacts (the server pairs an inbound number to a
		// contact when it can, but may leave `contactIds` empty if no
		// confident match was found).
		const findContactById = (id: string | undefined) => {
			if (!id || !contacts) return undefined;
			return contacts.find(c => c.id === id);
		};
		const findContactByPhone = (phone: string | undefined) => {
			if (!phone || !contacts) return undefined;
			return contacts.find(c => c.phones?.some(p => p.e164 === phone || p.standard === phone));
		};
		const contact = findContactById(callerContactId) ?? findContactByPhone(from);
		const callerName = contact?.name?.trim();

		if (company && callerName) return `${company} · ${callerName}`;
		return company || callerName || undefined;
	}, [
		ringingInboundBaseLead.data?.company,
		ringingInboundContacts.data,
		ringingInboundLookup.data?.contactIds,
		ringingInboundFrom,
	]);
	const attachCallMutation = useAttachCallToLead();

	const leadQuery = useLead(currentCompanyId);
	const baseLead = leadQuery.data;
	const { data: contacts } = useLeadContacts(currentCompanyId);
	const { sessionDialed, markDialed } = useDialedNumbers(currentCompanyId);
	const lead = useMemo(
		() => (baseLead && contacts ? withContacts(baseLead, contacts) : baseLead),
		[baseLead, contacts]
	);
	const nextLeadMutation = useNextLead();

	// Practice mode: serves REAL leads non-consuming (the backend's
	// `mode=PracticeCall` returns real leads but doesn't remove them from the
	// queue, so rehearsing on one doesn't burn it) and routes the call action to
	// an AI roleplay instead of a Twilio dial. State (active flag + practiced-id
	// dedup set) lives in `usePracticeMode`; scenarios are fetched only for coffee
	// accounts (the only place the tab is surfaced). `selectedScenarioId` is `null`
	// for a random scenario. See practice/usePracticeMode.ts and the
	// DRAWER_VALUE_PRACTICE wiring below.
	const practiceMode = usePracticeMode();
	const { data: practiceScenarios = [] } = useMyScenarios({ enabled: isCoffeeAccount });
	const { data: practiceSkillAreas = [] } = useMySkillAreas({ enabled: isCoffeeAccount });
	const { data: practiceCategories = [] } = useMyCategories({ enabled: isCoffeeAccount });
	const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
	// The contact a Spar should run against when started from the Practice panel.
	// Set when a contact's Spar button is clicked; resolved against the current
	// lead (falling back to its first contact) so the panel always has a target.
	const [sparTargetContactId, setSparTargetContactId] = useState<string | null>(null);

	const toggleDecisionMakerMutation = useToggleDecisionMaker();
	const deleteContactMutation = useDeleteContact();
	const addContactMutation = useAddContact();
	const updateContactMutation = useUpdateContact();
	const updateCompanyMutation = useUpdateCompany();
	const [editCompanyOpen, setEditCompanyOpen] = useState(false);
	const [companyPhonesOpen, setCompanyPhonesOpen] = useState(false);
	// Email composer: the contact being emailed (null = closed).
	const [composeContact, setComposeContact] = useState<FocusedLeadContact | null>(null);
	const handleComposeEmail = useCallback((contact: FocusedLeadContact) => setComposeContact(contact), []);
	const repTexting = useRepTextingNumber();
	const unreadTextQuery = useUnreadTextConversations(repTexting.textingNumber?.id, {
		enabled: repTexting.state === 'ready',
	});
	const unreadTextConversations = unreadTextQuery.data;
	const unreadTextCountByE164 = useMemo(
		() => (unreadTextConversations ? toUnreadCountByE164(unreadTextConversations) : undefined),
		[unreadTextConversations]
	);
	const textInbox = useTextInbox(unreadTextConversations);
	// No contact means the texter's number matched no contact, so the thread opens on the number alone.
	const [textTarget, setTextTarget] = useState<{
		contact: FocusedLeadContact | undefined;
		phone: FocusedLeadContactPhone;
	} | null>(null);
	const [textingBlockedOpen, setTextingBlockedOpen] = useState(false);

	// A notification link lands on a conversation the lead already replied in, so
	// it opens without the demo gate. Once per (companyId, contactId) pair.
	const openedTextForRef = useRef<string | null>(null);
	useEffect(() => {
		if (!queryOpenTextContactId) {
			openedTextForRef.current = null;
			return;
		}
		if (!currentCompanyId || repTexting.state !== 'ready') return;
		if (lead?.companyId !== currentCompanyId) return;
		const pairKey = `${currentCompanyId}|${queryOpenTextContactId}`;
		if (openedTextForRef.current === pairKey) return;
		const target = resolveOpenTextTarget(lead.contacts, queryOpenTextContactId, queryOpenTextPhone);
		if (!target) return;
		openedTextForRef.current = pairKey;
		setTextTarget(target);
		const remaining = { ...parse(location.search) };
		delete remaining.openText;
		delete remaining.openTextPhone;
		history.replace({ ...location, search: stringify(remaining) });
	}, [queryOpenTextContactId, queryOpenTextPhone, currentCompanyId, lead, repTexting.state, history, location]);
	// Contact dialog state — single dialog instance backs both add and edit modes.
	// `editingContactId` is null in add mode; the actual editing target is
	// looked up against `lead.contacts` so it stays in sync with cache updates.
	const [contactDialogState, setContactDialogState] = useState<{ mode: 'add' | 'edit'; contactId?: string } | null>(
		null
	);

	const handleSaveCompany = useCallback(
		(updates: { companyName: string; webSite: string }) => {
			const raw = baseLead?.rawCompany;
			if (!raw) return;
			updateCompanyMutation.mutate(
				{
					company: { ...raw, companyName: updates.companyName, webSite: updates.webSite },
				},
				{
					onSuccess: () => setEditCompanyOpen(false),
				}
			);
		},
		[baseLead?.rawCompany, updateCompanyMutation]
	);

	const handleSaveCompanyPhones = useCallback(
		(phoneNumbers: IPhoneNumber[]) => {
			const raw = baseLead?.rawCompany;
			if (!raw) return;
			updateCompanyMutation.mutate(
				{ company: { ...raw, phoneNumbers } },
				{ onSuccess: () => setCompanyPhonesOpen(false) }
			);
		},
		[baseLead?.rawCompany, updateCompanyMutation]
	);

	const handleToggleDecisionMaker = useCallback(
		(contact: FocusedLeadContact) => {
			if (!currentCompanyId || !baseLead) return;
			const currentDmIds = baseLead.decisionMakerIds;
			const isDM = currentDmIds.includes(contact.id);
			const nextDmIds = isDM ? currentDmIds.filter(id => id !== contact.id) : [...currentDmIds, contact.id];
			toggleDecisionMakerMutation.mutate({ companyId: currentCompanyId, contactIds: nextDmIds });
		},
		[currentCompanyId, baseLead, toggleDecisionMakerMutation]
	);

	const handleDeleteContact = useCallback(
		(contact: FocusedLeadContact) => {
			if (!currentCompanyId) return;
			if (!window.confirm(`Delete contact "${contact.name}"? This cannot be undone.`)) return;
			deleteContactMutation.mutate(
				{ companyId: currentCompanyId, contactId: contact.id },
				{
					onSuccess: () => toast.success('Contact deleted'),
					onError: err => toast.error(err.message || 'Failed to delete contact'),
				}
			);
		},
		[currentCompanyId, deleteContactMutation]
	);

	const handleOpenAddContact = useCallback(() => setContactDialogState({ mode: 'add' }), []);
	const handleOpenEditContact = useCallback(
		(contact: FocusedLeadContact) => setContactDialogState({ mode: 'edit', contactId: contact.id }),
		[]
	);
	const handleCloseContactDialog = useCallback((open: boolean) => {
		if (!open) setContactDialogState(null);
	}, []);

	// Translate the dialog's plain `ContactDraft` into the IContact shape the
	// backend expects. `phoneNumbers[].metadata` is intentionally left blank —
	// the server normalizes the value into e164/standard on save, just like the
	// legacy `EntityViewModel.mUpdate` flow.
	const draftToPhoneNumbers = useCallback(
		(phones: ContactDraft['phones']): IPhoneNumber[] => phones.map(p => ({ value: p.value, label: p.label })),
		[]
	);

	const handleSaveContact = useCallback(
		(draft: ContactDraft) => {
			if (!currentCompanyId || !contactDialogState) return;
			const phoneNumbers = draftToPhoneNumbers(draft.phones);
			// `primaryEmail` is a server-computed field derived from
			// `emailAddresses`; legacy `useSaveContact` never sends it and the
			// contact endpoint rejects a body that does — so we only send
			// `emailAddresses`.
			const emailAddresses = draft.email ? [{ value: draft.email }] : undefined;

			if (contactDialogState.mode === 'add') {
				const newContact: IContact = {
					firstName: draft.firstName || undefined,
					lastName: draft.lastName || undefined,
					jobTitle: draft.jobTitle || undefined,
					emailAddresses,
					phoneNumbers,
					companyId: currentCompanyId,
					company: baseLead?.rawCompany,
				};
				addContactMutation.mutate(
					{ companyId: currentCompanyId, contact: newContact },
					{
						onSuccess: () => {
							setContactDialogState(null);
							toast.success('Contact created');
						},
						onError: err => toast.error(apiErrorMessage(err, 'Failed to create contact')),
					}
				);
				return;
			}

			// Edit mode: merge over the existing IContact rather than rebuilding
			// from scratch so untouched fields (tags, integration data, etc.)
			// survive the round-trip. We pull the original contact off the
			// `useLeadContacts` cache so we don't have to re-fetch.
			const targetId = contactDialogState.contactId;
			if (!targetId) return;
			const existing = (contacts ?? []).find(c => c.id === targetId);
			// Build the edit body off the *raw* `IContact` (carried on the
			// projection as `.raw`) so untouched fields — tags, metadata,
			// integration data, etc. — survive the round-trip. The backend
			// rejects partial updates, so this mirrors the legacy
			// `useSaveContact` body `{ ...contact, company, companyId }`.
			const baseContact: IContact = existing?.raw ? { ...existing.raw } : { id: targetId };

			const updatedContact: IContact = {
				...baseContact,
				firstName: draft.firstName || undefined,
				lastName: draft.lastName || undefined,
				jobTitle: draft.jobTitle || undefined,
				emailAddresses,
				phoneNumbers,
				companyId: currentCompanyId,
				company: baseLead?.rawCompany,
			};

			updateContactMutation.mutate(
				{ companyId: currentCompanyId, contact: updatedContact },
				{
					onSuccess: () => {
						setContactDialogState(null);
						toast.success('Contact updated');
					},
					onError: err => toast.error(apiErrorMessage(err, 'Failed to update contact')),
				}
			);
		},
		[
			currentCompanyId,
			contactDialogState,
			addContactMutation,
			updateContactMutation,
			baseLead?.rawCompany,
			contacts,
			draftToPhoneNumbers,
		]
	);

	const editingContact =
		contactDialogState?.mode === 'edit' && contactDialogState.contactId
			? lead?.contacts.find(c => c.id === contactDialogState.contactId)
			: undefined;

	// Cell-phone picker state. The lookup itself fires through `useFindCellPhone`
	// (which already invalidates the contacts query on success); the picker
	// dialog displays the matches and lets the rep add one or flag a wrong
	// number. The dialog stays open for repeated actions until the rep closes
	// it explicitly — matches legacy `CellPhoneLookupModal` behavior.
	const [cellPhonePickerContact, setCellPhonePickerContact] = useState<FocusedLeadContact | null>(null);
	const [cellPhoneResult, setCellPhoneResult] = useState<ICellPhoneLookupResult | null>(null);
	const [cellPhoneError, setCellPhoneError] = useState<string | undefined>(undefined);
	const [cellPhoneAdded, setCellPhoneAdded] = useState<Set<string>>(new Set());
	const [cellPhoneAddingNumber, setCellPhoneAddingNumber] = useState<string | null>(null);
	const [cellPhoneMarkWrongPending, setCellPhoneMarkWrongPending] = useState<string | null>(null);

	const findCellPhoneMutation = useFindCellPhone({
		companyId: currentCompanyId,
		onSuccess: data => {
			setCellPhoneResult(data);
			setCellPhoneError(undefined);
			if (data.status !== 'Completed' || data.matches.length === 0) {
				// Keep the dialog open so the rep sees the empty state explicitly.
				toast.message(`No cell phone found for ${data.contactName}`);
			}
		},
		onError: err => {
			// Don't surface the raw API error to the rep — it can carry stack
			// traces / internal identifiers. Show a stable message and log
			// the underlying error for support to find in DevTools.
			setCellPhoneError('Cell phone lookup failed');
			toast.error('Cell phone lookup failed');
			console.error('Cell phone lookup error:', err);
		},
	});

	const markWrongNumberMutation = useMarkWrongNumberMutation({
		onSuccess: data => {
			// `wrongNumber` returns the same `ICellPhoneLookupResult` shape with
			// the picked match flagged — re-prime the picker with that.
			setCellPhoneResult(data);
			setCellPhoneMarkWrongPending(null);
		},
		onError: err => {
			setCellPhoneMarkWrongPending(null);
			toast.error('Failed to mark number as wrong');
			console.error('Mark-wrong-number error:', err);
		},
	});

	const handleFindCellPhone = useCallback(
		(contact: FocusedLeadContact) => {
			setCellPhonePickerContact(contact);
			setCellPhoneResult(null);
			setCellPhoneError(undefined);
			setCellPhoneAdded(new Set());
			setCellPhoneAddingNumber(null);
			setCellPhoneMarkWrongPending(null);
			findCellPhoneMutation.mutate(contact.id);
		},
		[findCellPhoneMutation]
	);

	const handleCloseCellPhonePicker = useCallback((open: boolean) => {
		if (!open) {
			setCellPhonePickerContact(null);
			setCellPhoneResult(null);
			setCellPhoneError(undefined);
		}
	}, []);

	const handleRetryCellPhoneLookup = useCallback(() => {
		if (!cellPhonePickerContact) return;
		setCellPhoneResult(null);
		setCellPhoneError(undefined);
		findCellPhoneMutation.mutate(cellPhonePickerContact.id);
	}, [cellPhonePickerContact, findCellPhoneMutation]);

	const handleAddCellPhoneNumber = useCallback(
		(match: ICellPhoneLookupMatch) => {
			if (!currentCompanyId || !cellPhonePickerContact) return;
			const target = (contacts ?? []).find(c => c.id === cellPhonePickerContact.id);
			const newPhone: IPhoneNumber = {
				label: 'Cell',
				value: match.phoneNumber,
				metadata: { e164: match.phoneNumber },
			};

			// Build the PUT body off the *raw* IContact (mirrors the edit path)
			// rather than rebuilding from the projection — the backend rejects
			// partial updates, so untouched fields (tags, metadata, integration
			// data, emails) must survive the round-trip. Bail loudly if raw is
			// unavailable rather than send a destructive partial body that would
			// silently wipe the other fields.
			const rawContact = target?.raw ?? cellPhonePickerContact.raw;
			if (!rawContact) {
				toast.error('Cannot add phone — contact data unavailable. Reopen the lead and try again.');
				return;
			}
			const updated: IContact = {
				...rawContact,
				companyId: currentCompanyId,
				company: baseLead?.rawCompany,
				phoneNumbers: [...(rawContact.phoneNumbers ?? []), newPhone],
			};

			setCellPhoneAddingNumber(match.phoneNumber);
			updateContactMutation.mutate(
				{ companyId: currentCompanyId, contact: updated },
				{
					onSuccess: () => {
						setCellPhoneAdded(prev => new Set(prev).add(match.phoneNumber));
						setCellPhoneAddingNumber(null);
						toast.success('Phone number added to contact');
					},
					onError: err => {
						setCellPhoneAddingNumber(null);
						toast.error(apiErrorMessage(err, 'Failed to add phone number'));
					},
				}
			);
		},
		[currentCompanyId, cellPhonePickerContact, contacts, baseLead?.rawCompany, updateContactMutation]
	);

	const handleMarkWrongNumber = useCallback(
		(match: ICellPhoneLookupMatch) => {
			if (!cellPhonePickerContact) return;
			setCellPhoneMarkWrongPending(match.phoneNumber);
			markWrongNumberMutation.mutate({ contactId: cellPhonePickerContact.id, phoneNumber: match.phoneNumber });
		},
		[cellPhonePickerContact, markWrongNumberMutation]
	);

	// Single funnel for advancing the queue. When `practice` is true we request a
	// non-consuming practice lead (`useNextLead({ practice: true, ... })` →
	// `mode=PracticeCall`, a REAL lead the backend leaves in the queue) and record
	// the served company id so it isn't repeated within the session; otherwise the
	// request and behaviour are exactly the standard queue pop. `loadNextLead` (and
	// every existing caller — Next-lead, skip, the URL effect, EmptyQueueState) goes
	// through here so practice mode is honoured uniformly; the explicit enter/exit
	// handlers pass `practice` directly rather than reading the not-yet-committed
	// `practiceMode.isActive`.
	const fetchNextLead = useCallback(
		(opts?: { practice?: boolean }) => {
			const practice = opts?.practice ?? false;
			const variables = practice
				? { practice: true, previousCompanyIds: [...practiceMode.practicedCompanyIds] }
				: undefined;
			nextLeadMutation.mutate(variables, {
				onSuccess: result => {
					if (result?.companyId) {
						if (practice) practiceMode.recordPracticed(result.companyId);
						setCurrentCompanyId(result.companyId);
						setQueueEmpty(false);
					} else {
						setCurrentCompanyId(undefined);
						setQueueEmpty(true);
					}
				},
				onError: () => {
					setQueueEmpty(true);
				},
			});
		},
		[nextLeadMutation, practiceMode]
	);

	const loadNextLead = useCallback(() => {
		fetchNextLead({ practice: practiceMode.isActive });
	}, [fetchNextLead, practiceMode.isActive]);

	// Enter practice mode and immediately serve the first practice lead. We pass
	// `practice: true` explicitly because `practiceMode.enter()` only schedules the
	// `isActive` state update — it isn't readable yet in this same tick.
	const handleEnterPractice = useCallback(() => {
		practiceMode.enter();
		fetchNextLead({ practice: true });
	}, [practiceMode, fetchNextLead]);

	// Leave practice mode and pull a real lead to return the rep to the standard
	// queue. Same rationale as enter: pass the flag explicitly (here `false`)
	// rather than relying on the freshly-scheduled `isActive` update.
	const handleExitPractice = useCallback(() => {
		practiceMode.exit();
		fetchNextLead({ practice: false });
	}, [practiceMode, fetchNextLead]);

	useEffect(() => {
		// Re-evaluate on every navigation (depending on `location.key` so the
		// effect fires even when the rep clicks a search result that produces
		// the same `companyId` already in the URL — without this, the dep
		// `queryCompanyId` is referentially stable as a primitive string and
		// the effect short-circuits, leaving `currentCompanyId` stuck at
		// undefined after queue exhaustion).
		if (queryCompanyId) {
			if (queryCompanyId !== currentCompanyId) {
				setCurrentCompanyId(queryCompanyId);
				setQueueEmpty(false);
			}
			return;
		}
		// URL no longer carries a companyId. Only auto-advance the queue when
		// we don't already have a lead loaded AND the queue isn't known to be
		// empty (rep just exhausted it — don't keep poking).
		if (!currentCompanyId && !queueEmpty) {
			loadNextLead();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryCompanyId, location.key]);

	// Next-lead and Exit both tear down the pipeline (Next via `loadNextLead`
	// → re-renders against a different companyId; Exit via `history.push`
	// → unmounts FocusedLeadView entirely). Block both while a call is in
	// flight so the rep doesn't accidentally drop their own call. Mirrors
	// legacy queue's `isPendingOutcome` gate, extended to cover all three
	// "call exists" CallState statuses (state machine in `call/state.ts`).
	const callIsActive =
		callState.status === 'connected' || callState.status === 'dialing' || callState.status === 'ringing';

	// Show in the tab strip which tab holds the dialer, and pulse it while a call rings.
	// A Spar practice session counts as in-call: the mic is live and the rep is occupied.
	const dialerTabActivity: DialerTabActivity =
		callState.status === 'ringing'
			? 'ringing'
			: callState.status === 'connected' || callState.status === 'dialing' || callState.status === 'practice'
				? 'in-call'
				: callState.status === 'uninit'
					? 'off'
					: 'ready';
	useDialerTabIndicator(dialerTabActivity);

	// Browsers block AudioContext until the user has interacted with the page.
	// initDevice() instantiates a Twilio Device which creates an AudioContext, so
	// we wait for the user to click "Enable call audio" in the CallStrip rather
	// than firing on mount.
	const handleEnableAudio = useCallback(() => {
		void initDevice();
	}, [initDevice]);

	// Auto-open the centered Enable Audio dialog on entry to the view (and any
	// later transition back to `uninit`, e.g. token expiration). Track dismissal
	// in a ref so the user can close it and use the inline CallStrip button
	// without the dialog popping back open. Dismissal resets once audio is on,
	// so the next time we land in `uninit` the prompt is prominent again.
	const audioDialogDismissedRef = useRef(false);
	const [audioDialogOpen, setAudioDialogOpen] = useState(false);

	const { snapshot: pacingSnapshot } = useDialPacing();
	const milestoneTrigger = useDialMilestoneTrigger(pacingSnapshot, { callIsActive });
	const adherence = useMyCoachingAdherenceQuery();
	// The judge trails the dial count, so pull a fresh number for the dialog rather than the last poll's.
	useEffect(() => {
		if (milestoneTrigger.open) void invalidateMyCoachingAdherenceQueries();
	}, [milestoneTrigger.open]);
	const recentCallsFeedback = useRecentCallsFeedback();
	const handleGetFeedback = () => {
		milestoneTrigger.close();
		appDrawerContext.setValue(DRAWER_VALUE_FEEDBACK);
		recentCallsFeedback.requestReview();
	};
	useEffect(() => {
		if (callState.status === 'uninit') {
			if (!audioDialogDismissedRef.current) setAudioDialogOpen(true);
		} else {
			setAudioDialogOpen(false);
			audioDialogDismissedRef.current = false;
		}
	}, [callState.status]);
	const handleAudioDialogOpenChange = useCallback((open: boolean) => {
		setAudioDialogOpen(open);
		if (!open) audioDialogDismissedRef.current = true;
	}, []);
	const handleEnableFromDialog = useCallback(() => {
		void initDevice();
		setAudioDialogOpen(false);
	}, [initDevice]);

	const primaryContact = lead?.contacts[0];

	// Display string forwarded to the CallStrip whenever a call is dialing,
	// ringing, or connected. Prefers `"Company · Contact Name"`; falls back
	// to whichever piece is available, or `undefined` (CallStrip then falls
	// back to the raw E.164 number).
	const peerDisplay = useMemo(() => {
		const company = lead?.company?.trim();
		const name = primaryContact?.name?.trim();
		if (company && name) return `${company} · ${name}`;
		return company || name || undefined;
	}, [lead?.company, primaryContact?.name]);

	// Active call's phoneCallId — when a real call is connected, thread it into
	// `useCoaching` so the existing conversation auto-enriches its creation
	// context with the phoneCallId. Mirrors legacy `useRealCallCoachingSession`.
	const activeCallPhoneCallId = callState.status === 'connected' ? callState.call.phoneCallId : undefined;
	const coaching = useCoaching({
		companyId: currentCompanyId,
		contactId: primaryContact?.id,
		contactName: primaryContact?.name,
		contactTitle: primaryContact?.title,
		phoneCallId: activeCallPhoneCallId,
		// Legitimately absent for search/share/inbound landings (see `data/types.ts` → `leadServedId`).
		leadServedId: lead?.leadServedId,
		// A failed lead fetch still lets coaching start, just without the served id.
		isLeadLoaded: leadQuery.isSuccess || leadQuery.isError,
		leadServedSource: queryLeadServedSource,
	});
	const {
		messages,
		isResponding,
		send,
		sendOpening,
		sendAutomatic,
		endCall,
		conversationId,
		addTranscriptEntry,
		requestAutoSuggestion,
		requestSuggestion,
		requestPostCallSummary,
		startNewConversation,
	} = coaching;

	// AI voice-roleplay ("Spar") wiring. The bridge fans the VoiceAgent's
	// finalized transcript lines out to any `TranscriptSource` subscriber; the
	// voice-agent source adapts those lines into the shared transcript pipeline.
	// Both are created once and held stable for the component's lifetime so the
	// pipeline doesn't tear down/re-subscribe on every render. `useSparSession`
	// is intentionally called AFTER `useCoaching` so `coaching.requestPostCallSummary`
	// (its only coaching dependency) is already resolved — the hook only needs
	// that single stable callback, so there's no circular dependency.
	const transcriptBridge = useMemo(() => createVoiceAgentTranscriptBridge(), []);
	const voiceAgentSource = useMemo(() => createVoiceAgentSource(transcriptBridge), [transcriptBridge]);
	const sparSession = useSparSession({
		companyId: currentCompanyId,
		leadServedId: lead?.leadServedId,
		pipeline,
		transcriptBridge,
		requestPostCallSummary,
	});

	// Auto-reclaim the live VoiceAgent if the FSM leaves `practice` for any reason
	// other than the End button — e.g. `device:unregistered` (token expiry/network
	// drop) flips the machine to `uninit`. Without this, the WebRTC connection stays
	// up and keeps pushing into an unsubscribed bridge with no UI to stop it. `end()`
	// is idempotent (see useSparSession), so depending on the booleans/status here —
	// not the unstable `sparSession.end` reference — is safe.
	useEffect(() => {
		if (sparSession.isActive && callState.status !== 'practice') {
			sparSession.end();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- depend on derived values, not the per-render-unstable sparSession; end() is idempotent
	}, [sparSession.isActive, callState.status]);

	// Any route change out of here unmounts FocusedLeadView, which tears down
	// `useCallPipeline` → destroys the Twilio Device → drops the call without a
	// logged outcome, so every leave path goes through this. Declared after
	// `useSparSession` so it can end a live Spar session first: `callIsActive`
	// doesn't cover practice (its own FSM state), and the unmount safety net only
	// frees the WebRTC/mic, losing the end-of-call summary.
	const guardLeavingView = useCallback((): boolean => {
		if (callIsActive) {
			toast.warning('End the active call first.');
			return false;
		}
		if (isPendingOutcome) {
			toast.warning('Log the call outcome before exiting.');
			appDrawerContext.setValue(DRAWER_VALUE_WRAPUP);
			return false;
		}
		if (sparSession.isActive) sparSession.end();
		return true;
	}, [callIsActive, isPendingOutcome, appDrawerContext, sparSession]);

	const handleOpenTextFromInbox = useCallback(
		(item: TextInboxItem) => {
			if (!item.companyId || !item.contactId) {
				if (!item.e164) return;
				setTextTarget({ contact: undefined, phone: numberOnlyPhone(item.e164, item.phoneStandard) });
				return;
			}
			if (item.companyId === currentCompanyId && lead?.companyId === currentCompanyId) {
				const target = resolveOpenTextTarget(lead.contacts, item.contactId, item.e164);
				if (target) {
					setTextTarget(target);
					return;
				}
			}
			if (!guardLeavingView()) return;
			setTextTarget(null);
			history.push({
				pathname: '/focused-view',
				search: `?${stringify({
					from: LeadServedSource.TextNotification,
					companyId: item.companyId,
					openText: item.contactId,
					openTextPhone: item.e164,
				})}`,
			});
			// HashHistory ignores a push to the URL it is already on, so sync the lead directly like the search panel.
			setCurrentCompanyId(item.companyId);
			setQueueEmpty(false);
		},
		[currentCompanyId, lead, guardLeavingView, history]
	);
	useNewTextToasts(textInbox, { onOpen: handleOpenTextFromInbox, openThreadE164: textTarget?.phone.e164 });

	const handleExit = useCallback(() => {
		if (!guardLeavingView()) return;
		// Exit to the call log rather than `/queue`: with Focused View enabled,
		// `/queue` redirects straight back here (see AidaRouter), so the call log
		// is the natural landing for "close the focused view".
		history.push('/call-log');
	}, [history, guardLeavingView]);

	const handleReturnToQueue = useCallback(() => {
		if (!guardLeavingView()) return;
		setFocusedViewEnabled(false);
		history.push('/queue');
	}, [history, setFocusedViewEnabled, guardLeavingView]);

	// Outbound call from a contact row. Two destinations depending on mode:
	//   - Practice mode ON  → start an AI voice-roleplay ("Spar") session with
	//     this contact + the selected scenario (random when none is picked).
	//     No real Twilio dial happens; the pipeline enters the `practice` FSM
	//     state instead. The pending-outcome gate doesn't apply (practice
	//     sessions don't produce a wrap-up outcome).
	//   - Practice mode OFF → the real dial, gated on coaching readiness via
	//     `dialWithCoachingGate` (mirrors legacy `startCoachingBeforeCall`).
	//     Routes through the focused view's own pipeline so the legacy
	//     `TelephonyContext` stays out of this module. The
	//     `telephonyConfigurationId` is required — the server uses it to
	//     pre-create the phone-call record before the Twilio gateway accepts
	//     the connection.
	//
	// Declared here (after `useSparSession`) rather than alongside the other
	// contact handlers above so the closure can reference `sparSession` without
	// a use-before-declaration error — it's only invoked from JSX, well after
	// every dependency is initialized.

	// Ref mirror of the coaching readiness fields so the pre-dial gate's poll
	// loop reads fresh values without re-creating the dial handlers — the
	// legacy `CallButton`'s `coachingRef` pattern.
	const coachingReadinessRef = useRef({
		conversationId: coaching.conversationId,
		isResponding: coaching.isResponding,
		isCreatingConversation: coaching.isCreatingConversation,
	});
	coachingReadinessRef.current = {
		conversationId: coaching.conversationId,
		isResponding: coaching.isResponding,
		isCreatingConversation: coaching.isCreatingConversation,
	};
	// Re-entrancy guard: while the gate is waiting on the coaching bootstrap
	// (up to 15s), the FSM is still `idle`, so without this a second click
	// would queue a second dial. Legacy equivalent: `isStartingCoaching`
	// nulling the CallButton's onClick. The ref guards synchronously (state
	// updates are async); `dialPending` drives the button-disabled UI below.
	const dialGateBusyRef = useRef(false);
	// Disables the Call affordances while the gate waits, matching the legacy
	// `CallButton`'s `isStartingCoaching` disabled state. Only observable in the
	// rare case where the conversation bootstrap hasn't settled at click time —
	// the fast path resolves synchronously and never flips this on.
	const [dialPending, setDialPending] = useState(false);

	// Shared dial path for both contact-row and company-phone calls. Waits for
	// the coaching conversation bootstrap to settle (legacy
	// `startCoachingBeforeCall` semantics — immediate when already ready, polls
	// up to 15s otherwise, dials regardless on timeout) so the `POST phonecall`
	// carries `aiConversationId` and the server writes the reverse
	// `PhoneCall.AIConversationId` link at creation. Without that link the
	// post-call adherence check silently skips the call.
	const dialWithCoachingGate = useCallback(
		async (e164: string, params: { companyId: string; contactId?: string }) => {
			if (dialGateBusyRef.current) return;
			// Mark dialed optimistically — the transcription-filtered call history lags a just-placed call.
			markDialed(e164);
			dialGateBusyRef.current = true;
			setDialPending(true);
			try {
				const aiConversationId = await waitForCoachingConversation(() => coachingReadinessRef.current);
				void pipeline.dial(e164, {
					...params,
					// Must stay non-null so the server can pre-create the phone-call record.
					telephonyConfigurationId:
						baseLead?.suggestedTelephonyConfigurationId ?? userSession.telephonyConfiguration?.id,
					aiConversationId: aiConversationId ?? undefined,
				});
			} finally {
				dialGateBusyRef.current = false;
				setDialPending(false);
			}
		},
		[pipeline, baseLead?.suggestedTelephonyConfigurationId, userSession.telephonyConfiguration?.id, markDialed]
	);

	const handleTextContact = useCallback(
		(contact: FocusedLeadContact, phone: FocusedLeadContactPhone) => {
			if (!hasActiveDemo(lead)) {
				setTextingBlockedOpen(true);
				return;
			}
			setTextTarget({ contact, phone });
		},
		[lead]
	);

	const handleCallContact = useCallback(
		(contact: FocusedLeadContact, phone: FocusedLeadContactPhone) => {
			if (!currentCompanyId) return;
			if (practiceMode.isActive) {
				// A live real call owns the pipeline; starting a Spar session would
				// mint a wasted startPracticeCall POST + a transient hot-mic WebRTC
				// session that self-heals via auto-reclaim but gives the rep no
				// feedback. Block it and tell them to end the call first.
				if (callIsActive) {
					toast.error('End the active call first');
					return;
				}
				const scenario = practiceScenarios.find(s => s.id === selectedScenarioId) ?? null;
				void sparSession.start({
					contact: {
						id: contact.id,
						firstName: contact.firstName,
						lastName: contact.lastName,
						name: contact.name,
						title: contact.title,
					},
					scenario,
				});
				return;
			}
			// Same pending-outcome gate as `handleCallCompanyPhone` — see I1.
			if (isPendingOutcome) {
				toast.warning('Log the call outcome before dialing again.');
				appDrawerContext.setValue(DRAWER_VALUE_WRAPUP);
				return;
			}
			// Wait for the coaching conversation before dialing (legacy
			// `startCoachingBeforeCall` behavior) so the reverse
			// `PhoneCall.AIConversationId` link is set at creation. The post-connect
			// enrich in `useCoaching` still sets the forward link; this is additive.
			void dialWithCoachingGate(phone.e164, {
				companyId: currentCompanyId,
				contactId: contact.id,
			});
		},
		[
			currentCompanyId,
			practiceMode.isActive,
			practiceScenarios,
			selectedScenarioId,
			sparSession,
			isPendingOutcome,
			appDrawerContext,
			callIsActive,
			dialWithCoachingGate,
		]
	);

	// Declared after `useCoaching` (like `handleCallContact`) so the closure can
	// reference `dialWithCoachingGate` (which reads the coaching readiness ref)
	// without a use-before-declaration error. Only invoked from JSX, well after
	// every dependency is initialized.
	const handleCallCompanyPhone = useCallback(
		(phone: FocusedLeadContactPhone) => {
			if (!currentCompanyId) return;
			// Pending-outcome gate: legacy `CallActions` swaps the Call button
			// for a "Log last call outcome" pill while `isPendingOutcome` is
			// true so the rep can't dial again without logging the previous
			// call. Without this, dismissing the wrap-up drawer leaves Call
			// enabled and lets the rep dial past an unlogged call.
			if (isPendingOutcome) {
				toast.warning('Log the call outcome before dialing again.');
				appDrawerContext.setValue(DRAWER_VALUE_WRAPUP);
				return;
			}
			// Wait for the coaching conversation before dialing (legacy
			// `startCoachingBeforeCall` behavior) so the reverse
			// `PhoneCall.AIConversationId` link is set at creation; the post-connect
			// enrich still sets the forward link.
			void dialWithCoachingGate(phone.e164, { companyId: currentCompanyId });
		},
		[currentCompanyId, isPendingOutcome, appDrawerContext, dialWithCoachingGate]
	);

	const handleCallCellPhoneNumber = useCallback(
		(match: ICellPhoneLookupMatch) => {
			if (!currentCompanyId || !cellPhonePickerContact) return;
			if (isPendingOutcome) {
				toast.warning('Log the call outcome before dialing again.');
				appDrawerContext.setValue(DRAWER_VALUE_WRAPUP);
				return;
			}
			const contactId = cellPhonePickerContact.id;
			setCellPhonePickerContact(null);
			setCellPhoneResult(null);
			setCellPhoneError(undefined);
			void dialWithCoachingGate(match.phoneNumber, { companyId: currentCompanyId, contactId });
		},
		[currentCompanyId, cellPhonePickerContact, isPendingOutcome, appDrawerContext, dialWithCoachingGate]
	);

	// Ad-hoc Spar entry: clicking a contact's Spar button no longer starts a call
	// immediately. It stashes that contact as the practice target and opens the
	// Practice Mode drawer tab so the rep picks a scenario first; the panel's
	// "Start practicing" button (→ `handleStartSpar`) actually starts the session.
	const handleSparContact = useCallback(
		(contact: FocusedLeadContact) => {
			setSparTargetContactId(contact.id);
			appDrawerContext.setValue(DRAWER_VALUE_PRACTICE);
		},
		[appDrawerContext]
	);

	// The contact a panel-launched Spar runs against: the explicitly-clicked
	// contact, else the first contact on the current lead. Never invented — if the
	// lead has no contacts this is `undefined` and the panel's Start is disabled.
	const sparTargetContact = useMemo(
		() => lead?.contacts.find(c => c.id === sparTargetContactId) ?? lead?.contacts[0],
		[lead, sparTargetContactId]
	);

	// Start an ad-hoc Spar from the Practice panel against the resolved target
	// contact with the selected scenario. Always a practice session (never a
	// Twilio dial), so it skips the pending-outcome gate and produces no CRM
	// wrap-up outcome — the rep relies on the AI end-of-call summary instead.
	const handleStartSpar = useCallback(() => {
		// A live real call owns the pipeline; don't mint a wasted practice session
		// on top of it.
		if (callIsActive) {
			toast.error('End the active call first');
			return;
		}
		const contact = sparTargetContact;
		if (!contact) return;
		const scenario = practiceScenarios.find(s => s.id === selectedScenarioId) ?? null;
		void sparSession.start({
			contact: {
				id: contact.id,
				firstName: contact.firstName,
				lastName: contact.lastName,
				name: contact.name,
				title: contact.title,
			},
			scenario,
		});
	}, [sparSession, practiceScenarios, selectedScenarioId, callIsActive, sparTargetContact]);

	// Realtime-coaching websocket status for the "is this window receiving
	// Twilio webhooks?" indicator surfaced next to the hero eyebrow (C6).
	const wsStatus = useWebsocketConnectionStatus();
	const isWsConnected = wsStatus === Api.RemoteEventsConnectionStatus.Connected;

	// Tracks whether the "Get summary" affordance should be shown above the
	// pinned-card stack. Set on `call:ended`, cleared once the rep clicks
	// the button or a fresh dial begins.
	const [callEndedPendingSummary, setCallEndedPendingSummary] = useState(false);

	// User preference: when enabled, every lead-final transcript triggers an
	// AI suggestion request (gated to avoid overlap with an in-flight stream).
	// Falls back to the legacy `coffeePreferences.autoSuggestionsEnabled` flag
	// so changing the toggle in the Queue settings affects both views.
	const autoSuggestionsEnabled = userSession.user?.userPreferences?.coffeePreferences?.autoSuggestionsEnabled ?? false;

	// Provider-agnostic transcript pipeline. A real call streams from Twilio; an
	// AI practice ("Spar") session streams from the VoiceAgent via the bridge.
	// We swap the active source set based on call status rather than mounting
	// both at once — only one of the two can be live at a time, and keeping the
	// inactive source out of the array avoids it subscribing needlessly. Adding
	// another real-call source (Gong, Otter, in-house ASR) is a matter of
	// dropping another `TranscriptSource` into the non-practice branch. See
	// `transcript/README.md` for the recipe.
	const isPractice = callState.status === 'practice';
	const transcriptSources = useMemo<TranscriptSource[]>(
		() => (isPractice ? [voiceAgentSource] : [twilioTranscriptSource]),
		[isPractice, voiceAgentSource]
	);
	const activeCall = callState.status === 'connected' ? callState.call : undefined;
	// During practice the only context the voice-agent source needs is the
	// server phoneCallId (Spar entries already carry their own speaker, and
	// there's no inbound/outbound direction). For a real call we forward the
	// Twilio phoneCallId + direction as before.
	const practiceCallId = callState.status === 'practice' ? callState.session.phoneCallId : undefined;
	const transcriptContext = useMemo(
		() =>
			isPractice
				? { phoneCallId: practiceCallId }
				: { phoneCallId: activeCall?.phoneCallId, direction: activeCall?.direction },
		[isPractice, practiceCallId, activeCall?.phoneCallId, activeCall?.direction]
	);
	const handleTranscriptFinal = useCallback(
		(entry: TranscriptEntry) => {
			addTranscriptEntry({
				id: entry.id,
				speaker: entry.speakerLabel ?? entry.speaker,
				text: entry.text,
				timestamp: entry.timestamp,
			});
			// Auto-suggest on lead-final utterances. Mirrors the legacy
			// `useRealCallCoaching` behavior: rep-side audio doesn't trigger;
			// the queue gate handles "AI is currently responding".
			if (autoSuggestionsEnabled && entry.speaker === 'lead') {
				requestAutoSuggestion();
			}
		},
		[addTranscriptEntry, autoSuggestionsEnabled, requestAutoSuggestion]
	);
	useTranscriptStream({
		sources: transcriptSources,
		// Stream is live for both a real connected call and an AI practice
		// session. `handleTranscriptFinal` is reused unchanged for practice —
		// addTranscriptEntry + auto-suggest are exactly what we want there too.
		enabled: callState.status === 'connected' || callState.status === 'practice',
		context: transcriptContext,
		onFinal: handleTranscriptFinal,
	});

	// We need the phoneCallId from the active call to seed the Wrap-up panel after
	// the call ends — but by the time `call:ended` fires the connected state is
	// gone. Stash it in a ref on `call:connected` and promote to state on
	// `call:ended` so the drawer that auto-opens has a valid id to submit against.
	const activeCallRef = useRef<{ phoneCallId?: string } | null>(null);
	useEffect(() => {
		// Capture the inbound phone-call id + raw E.164 on ringing so the
		// CallStrip can resolve the caller against our lead store while it's
		// still ringing (otherwise the strip falls through to the raw
		// number). Floating sonner toast was removed earlier — the strip is
		// the single source.
		const offIncoming = pipeline.subscribe('call:incoming', payload => {
			if (payload.phoneCallId) setRingingInboundPhoneCallId(payload.phoneCallId);
			if (payload.from) setRingingInboundFrom(payload.from);
		});
		const offConnected = pipeline.subscribe('call:connected', info => {
			activeCallRef.current = { phoneCallId: info?.phoneCallId };
			// Once the call is live the CallStrip switches to the connected
			// state and reads from `peer`, so we no longer need the ringing
			// lookup state hanging around.
			setRingingInboundPhoneCallId(undefined);
			setRingingInboundFrom(undefined);
			// CallFromUnknown trigger: if this is an inbound call and we have a
			// phoneCallId, capture it so the usePhoneCallById effect below can
			// resolve whether the caller is known and open rescue search if not.
			if (info && info.direction === 'inbound' && info.phoneCallId) {
				setUnknownInboundCall({ phoneCallId: info.phoneCallId, from: info.peer });
			}
		});
		const offEnded = pipeline.subscribe('call:ended', () => {
			// INVARIANT: practice/Spar sessions never reach this handler. They run
			// on the `call:practice-started` / `call:practice-ended` events and the
			// FSM moves `idle → practice → idle` directly (see call/state.ts) — it
			// never enters `ended` and `call:ended` is emitted only from the real
			// Twilio paths (call/twilio.ts, call/websocket.ts). That is what keeps
			// practice from auto-opening Wrap-up (line below) or bumping dial pacing
			// (the `invalidateQueries(pacing)` below): a Spar session writes no CRM
			// outcome and relies on the AI end-of-call summary instead. If a future
			// change ever routes practice teardown through `call:ended`, gate the
			// Wrap-up + pacing lines here on "was this a practice session?".
			const summaryQueued = endCall();
			// Drop any rescue capture so the search drawer doesn't reopen on a
			// stale phone-call id when the next call comes in.
			setUnknownInboundCall(null);
			setRingingInboundPhoneCallId(undefined);
			setRingingInboundFrom(undefined);
			// Promote the captured phoneCallId so WrapupPanel can submit against it.
			if (activeCallRef.current?.phoneCallId) {
				setLastEndedPhoneCallId(activeCallRef.current.phoneCallId);
			}
			// `endCall` already dispatched the summary for any call that carried
			// transcript, so the button is only an override for the case it
			// short-circuited on (dial failure, instant hangup, transcription
			// pipeline miss).
			setCallEndedPendingSummary(!summaryQueued);
			// Latch the pending-outcome flag so Next-lead / Exit are gated
			// until the wrap-up submits. Cleared in `handleWrapupSubmitted`.
			setIsPendingOutcome(true);
			// A call that ended while reconnecting still needs to clear the
			// reconnect indicator — otherwise the next idle/ringing strip would
			// inherit the amber treatment.
			setReconnecting(false);
			// Wrap-up bumps the rep's dial/connection/demo counts on the server, so
			// invalidate the pacing query immediately rather than waiting for the
			// 60s refetch to land.
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.pacing() });
			// Always land the rep on Wrap-up after a call, even though force-switching the drawer is jarring.
			appDrawerContext.setValue(DRAWER_VALUE_WRAPUP);
		});
		// `call:reconnecting` fires when Twilio loses signaling on an active call.
		// Surface a non-blocking amber pill on the CallStrip so the rep sees the
		// audio dropout (vs. being met with sudden silence) and `call:reconnected`
		// clears it once the SDK recovers. Both events fire repeatedly during a
		// flaky network so the simple boolean is sufficient — no debounce.
		const offReconnecting = pipeline.subscribe('call:reconnecting', () => setReconnecting(true));
		const offReconnected = pipeline.subscribe('call:reconnected', () => setReconnecting(false));
		// `device:error` fires for non-recoverable Twilio Device-level errors
		// (token failures, offline, gateway rejects). The TwilioController
		// already filters out auto-recoverable codes (31002 / 31202), so any
		// payload that lands here is something the rep should know about.
		const offDeviceError = pipeline.subscribe('device:error', payload => {
			toast.error(payload.message || 'Call audio device error');
		});
		return () => {
			offIncoming();
			offConnected();
			offEnded();
			offReconnecting();
			offReconnected();
			offDeviceError();
		};
	}, [pipeline, endCall, queryClient, appDrawerContext]);

	// Auto-fire `before_call` keyword pins exactly once per conversation id.
	// Dedupe is persisted via `hasFiredBeforeCallPins` so revisiting a lead
	// (component remount, full reload) doesn't re-send GAME PLAN on top of
	// the prior reply. The in-memory ref is a cheap fast-path for the same
	// conversation within a single mount.
	const beforeCallFiredForRef = useRef<string | null>(null);
	useEffect(() => {
		if (!conversationId) return;
		if (beforeCallFiredForRef.current === conversationId) return;
		if (hasFiredBeforeCallPins(conversationId)) {
			beforeCallFiredForRef.current = conversationId;
			return;
		}
		beforeCallFiredForRef.current = conversationId;
		markBeforeCallPinsFired(conversationId);
		// The opening goes out even with the Game Plan pin off, so every fresh conversation gets its Game Plan.
		const { gamePlan, others } = splitBeforeCallPins(keywordPins);
		if (gamePlan) {
			sendOpening(gamePlan.prompt, { pinnedKeyword: gamePlan.label.toUpperCase(), pinColor: gamePlan.color });
		} else {
			sendOpening();
		}
		for (const pin of others) {
			sendAutomatic(pin.prompt, { pinnedKeyword: pin.label.toUpperCase(), pinColor: pin.color });
		}
	}, [conversationId, keywordPins, sendOpening, sendAutomatic]);

	// Auto-fire `on_dial` keyword pins each time an outbound dial begins. Fires
	// regardless of resume state — these pins are scoped to the call, not the
	// conversation lifetime.
	useEffect(() => {
		const unsub = pipeline.subscribe('call:dialing', () => {
			// A fresh dial means any post-call summary affordance from a prior
			// call is now stale — clear it so we don't show "Get summary" on
			// top of a brand-new conversation.
			setCallEndedPendingSummary(false);
			// Same for the pending-outcome gate: starting a new call obsoletes
			// the prior unresolved outcome (the rep dialed past it explicitly,
			// matching legacy queue behavior where a new dial unblocks Next).
			setIsPendingOutcome(false);
			for (const pin of keywordPins) {
				if (!pin.enabled || pin.trigger !== 'on_dial') continue;
				sendAutomatic(pin.prompt, { pinnedKeyword: pin.label.toUpperCase(), pinColor: pin.color });
			}
		});
		return unsub;
	}, [pipeline, keywordPins, sendAutomatic]);

	// Click-handler wraps the hook method so we can clear the "Get summary"
	// affordance the moment the rep dispatches. The summary streams through
	// the same `useCoaching` send queue so a concurrent reply finishes first.
	const handleRequestSummary = useCallback(() => {
		requestPostCallSummary();
		setCallEndedPendingSummary(false);
	}, [requestPostCallSummary]);

	// Auto-pin two cases (each id only ever auto-pinned once, so an explicit
	// user-unpin is sticky):
	//   1. The very first assistant reply — becomes the Game Plan card.
	//   2. Any assistant reply that arrives carrying a `pinnedKeyword` (i.e. it
	//      came from a keyword-pin trigger / starter row).
	// See pins/README.md → "Auto-pin contract" for the full flow.
	const autoPinnedIdsRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		const firstAssistant = messages.find(m => m.role === 'assistant');
		if (firstAssistant && !autoPinnedIdsRef.current.has(firstAssistant.id)) {
			autoPinnedIdsRef.current.add(firstAssistant.id);
			pinMessage(firstAssistant.id);
		}
		for (const m of messages) {
			if (m.role !== 'assistant' || !m.pinnedKeyword) continue;
			if (autoPinnedIdsRef.current.has(m.id)) continue;
			autoPinnedIdsRef.current.add(m.id);
			pinMessage(m.id);
		}
	}, [messages, pinMessage]);

	// Decorate the first assistant message with Game Plan styling so the rail
	// renders it with the orange accent. Leaves messages that already carry a
	// keyword tag (from a keyword-pin trigger) alone.
	const decoratedMessages = useMemo(() => {
		const idx = messages.findIndex(m => m.role === 'assistant');
		if (idx < 0) return messages;
		const target = messages[idx];
		if (target.pinnedKeyword) return messages;
		return messages.map((m, i) =>
			i === idx ? { ...m, pinnedKeyword: 'GAME PLAN', pinColor: 'var(--pin-orange)' } : m
		);
	}, [messages]);

	// When no lead is currently loaded (queue exhausted, or the user is between
	// search picks), don't surface the *previous* lead's pinned cards / hero in
	// the CENTER column or the AI Conversation drawer. The CallStrip still
	// renders so "Listening for incoming calls" stays visible, but the
	// coaching surfaces go blank — matching the LEFT column's empty state.
	const displayMessages = currentCompanyId ? decoratedMessages : [];
	const displayPinnedIds = currentCompanyId ? pinnedIds : [];

	// Clear the local pinned-card session when the active lead clears so the
	// CENTER column doesn't keep the previous lead's pins hanging around
	// visually (the gate above hides them; this also drops the underlying
	// ids so a future revisit starts clean).
	useEffect(() => {
		if (currentCompanyId) return;
		if (pinnedIds.length === 0) return;
		clearPinned();
	}, [currentCompanyId, pinnedIds.length, clearPinned]);

	// Track unread assistant messages while the AI drawer tab is not active.
	// Whenever the AI tab is open, the most recent assistant message id is marked
	// as "seen" — the badge resets to 0 the moment the rep opens the tab and only
	// counts assistant turns that arrive while it's closed.
	const aiTabActive = appDrawerContext.value === DRAWER_VALUE_AI;
	const [lastSeenAssistantId, setLastSeenAssistantId] = useState<string | null>(null);
	useEffect(() => {
		if (aiTabActive) {
			// Avoid `findLast` for Firefox 91 ESR (browserslist target).
			const lastAssistant = [...decoratedMessages].reverse().find(m => m.role === 'assistant');
			setLastSeenAssistantId(lastAssistant?.id ?? null);
		}
	}, [aiTabActive, decoratedMessages]);

	const unreadCount = useMemo(() => {
		if (aiTabActive) return 0;
		if (!lastSeenAssistantId) return decoratedMessages.filter(m => m.role === 'assistant').length;
		const idx = decoratedMessages.findIndex(m => m.id === lastSeenAssistantId);
		if (idx === -1) return decoratedMessages.filter(m => m.role === 'assistant').length;
		return decoratedMessages.slice(idx + 1).filter(m => m.role === 'assistant').length;
	}, [aiTabActive, decoratedMessages, lastSeenAssistantId]);

	const handleSend = useCallback(
		(text: string) => {
			const match = matchKeywordIn(keywordPins, text);
			if (match) {
				send(match.prompt, { pinnedKeyword: match.label.toUpperCase(), pinColor: match.color });
			} else {
				send(text);
			}
		},
		[keywordPins, send]
	);

	const handleSendStarter = useCallback(
		(pin: KeywordPin) => {
			send(pin.prompt, { pinnedKeyword: pin.label.toUpperCase(), pinColor: pin.color });
		},
		[send]
	);

	// Past-transcripts drawer hands us a label + the joined speaker lines for
	// the chosen call. We prepend a short header so the AI knows the user is
	// asking about a historical conversation (vs. the live call), then flip
	// the drawer to the AI tab so the rep sees the message land.
	const handleSendPastTranscript = useCallback(
		(label: string, transcriptText: string) => {
			const prelude = `I'm sending you a transcript from a previous call (${label}) so I can ask you questions about it:`;
			send(`${prelude}\n\n${transcriptText}`);
			appDrawerContext.setValue(DRAWER_VALUE_AI);
		},
		[send, appDrawerContext]
	);

	const handleNextLead = useCallback(() => {
		if (callIsActive) {
			toast.warning('End the active call first.');
			return;
		}
		if (isPendingOutcome) {
			toast.warning('Log the call outcome before moving on.');
			// Surface the wrap-up drawer so the rep can act on the prompt.
			appDrawerContext.setValue(DRAWER_VALUE_WRAPUP);
			return;
		}
		// Moving past a lead with no outcome is a skip, so it goes through the skip reasons that skip-rate
		// reporting counts rather than silently serving the next lead.
		if (currentCompanyId && !hasDispositioned && !practiceMode.isActive) {
			setSkipDialogMode(undefined);
			setSkipDialogOpen(true);
			return;
		}
		loadNextLead();
	}, [
		loadNextLead,
		callIsActive,
		isPendingOutcome,
		appDrawerContext,
		currentCompanyId,
		hasDispositioned,
		practiceMode.isActive,
	]);

	// CallFromUnknown rescue effect: once `usePhoneCallById` resolves for an
	// inbound capture, decide whether the caller is known. A populated
	// `companyId` means the server already linked the lead — point the
	// focused view at that lead (the rep may have been on a different one)
	// and drop rescue state. An empty `companyId` is the rescue signal: open
	// the SearchPanel so the rep can attach the call to a lead.
	useEffect(() => {
		if (!unknownInboundCall) return;
		if (!inboundLookup.data) return;
		const resolvedCompanyId = inboundLookup.data.companyId;
		if (resolvedCompanyId) {
			if (resolvedCompanyId !== currentCompanyId) {
				setCurrentCompanyId(resolvedCompanyId);
				setQueueEmpty(false);
			}
			setUnknownInboundCall(null);
			return;
		}
		appDrawerContext.setValue(DRAWER_VALUE_SEARCH);
	}, [unknownInboundCall, inboundLookup.data, appDrawerContext, currentCompanyId]);

	// TopBar "Skip lead" entry-point handler. Pending-outcome / active-call
	// gates are intentionally lifted from the existing handleNextLead path
	// so all forward-motion buttons behave consistently.
	const handleOpenSkipDialog = useCallback(
		(mode?: 'skip' | 'remove') => {
			if (!currentCompanyId) return;
			if (callIsActive) {
				toast.warning('End the active call first.');
				return;
			}
			if (isPendingOutcome) {
				toast.warning('Log the call outcome before skipping.');
				appDrawerContext.setValue(DRAWER_VALUE_WRAPUP);
				return;
			}
			setSkipDialogMode(mode);
			setSkipDialogOpen(true);
		},
		[currentCompanyId, callIsActive, isPendingOutcome, appDrawerContext]
	);

	const handlePickSkipReason = useCallback(
		async (reason: ISkipReason, noteHtml?: string) => {
			if (!currentCompanyId) return;
			setPendingSkipReasonId(reason.id);

			// Reasons flagged `requireNote`/`requireCustomReason` hand back the note
			// HTML; save it first and thread the resulting `noteId` into the skip so
			// the server doesn't reject the note-required skip (mirrors the wrap-up
			// BadLead path). The note save and the skip aren't atomic, so we cache
			// the saved note id keyed by its HTML: if the skip fails and the rep
			// retries the same note, we reuse the existing note instead of POSTing a
			// duplicate.
			let noteId: string | undefined;
			if (noteHtml && !isRichTextEmpty(noteHtml)) {
				if (skipNoteCacheRef.current?.html === noteHtml) {
					noteId = skipNoteCacheRef.current.noteId;
				} else {
					try {
						const savedNote = await saveSkipNoteMutation.mutateAsync({
							phoneCallId: 'manual',
							companyId: currentCompanyId,
							contentHtml: noteHtml,
							isForOutcome: true,
						});
						noteId = savedNote.id ?? undefined;
						if (noteId) skipNoteCacheRef.current = { html: noteHtml, noteId };
					} catch (noteErr) {
						// Distinguish the note-save failure from a skip failure — the
						// skip never ran, so "Failed to skip lead" would mislead.
						setPendingSkipReasonId(null);
						toast.error(apiErrorMessage(noteErr, 'Failed to save note'));
						return;
					}
				}
			}

			try {
				await skipLeadMutation.mutateAsync({
					companyId: currentCompanyId,
					followUp: null,
					currentCallId: 'manual',
					outcomeId: reason.id,
					skipReason: reason.name,
					leadServedSource: queryLeadServedSource,
					noteId,
				});

				skipNoteCacheRef.current = null;
				setPendingSkipReasonId(null);
				setSkipDialogOpen(false);
				toast.success(reason.excludesFromPool ? 'Lead removed' : 'Lead skipped');
				loadNextLead();
			} catch (err) {
				setPendingSkipReasonId(null);
				toast.error(apiErrorMessage(err, 'Failed to skip lead'));
			}
		},
		[currentCompanyId, queryLeadServedSource, skipLeadMutation, saveSkipNoteMutation, loadNextLead]
	);

	// Rescue-mode attach handler. The SearchPanel hands us back the chosen
	// company; we run useAttachCallToLead (optimistic cache patch + lead
	// invalidation) then point the focused view at the rescued lead so the
	// LEFT/CENTER columns rerender and wrap-up has a companyId to submit
	// against on call:ended.
	const handleRescueAttach = useCallback(
		(companyId: string) => {
			if (!unknownInboundCall) return;
			attachCallMutation.mutate(
				{ phoneCallId: unknownInboundCall.phoneCallId, companyId },
				{
					onSuccess: () => {
						setCurrentCompanyId(companyId);
						setUnknownInboundCall(null);
						appDrawerContext.setValue('');
						toast.success('Call attached to lead');
					},
					onError: err => {
						toast.error(err.message || 'Failed to attach call');
					},
				}
			);
		},
		[unknownInboundCall, attachCallMutation, appDrawerContext]
	);

	// Wrap-up submitted (non-Booked outcome) OR a Booked-class deal was just
	// created. Close the drawer and clear the pending-outcome gate so Next /
	// Exit unblock — but DON'T auto-advance to the next lead. A lead may have
	// multiple contacts the rep wants to try; auto-advancing the queue after
	// every wrap-up forces the rep to navigate back to the same company to
	// dial another contact. The rep clicks "Next lead" explicitly when ready.
	// WrapupPanel handles the Booked → DealForm body switch internally and
	// fires this callback once useCreateDeal resolves.
	const handleWrapupSubmitted = useCallback(() => {
		setIsPendingOutcome(false);
		setHasDispositioned(true);
		appDrawerContext.setValue('');
	}, [appDrawerContext]);

	const handleSavePin = useCallback(
		(patch: SavePinPatch) => {
			if (patch.id) {
				update(patch.id, {
					label: patch.label,
					keyword: patch.keyword,
					color: patch.color,
					prompt: patch.prompt,
					trigger: patch.trigger,
					enabled: patch.enabled,
				});
			} else {
				create({
					label: patch.label,
					keyword: patch.keyword,
					color: patch.color,
					prompt: patch.prompt,
					trigger: patch.trigger,
					enabled: patch.enabled,
				});
			}
			setEditing(null);
		},
		[create, update]
	);

	return (
		<div data-focused-view className={css(styles.root, styles.focusedViewRoot)}>
			<div className={css(styles.app)} style={{ gridTemplateColumns: `${leftColWidth}px minmax(0, 1fr) auto` }}>
				<div className={css(styles.colLeft)}>
					<ColumnResizeHandle width={leftColWidth} onResize={setLeftColWidth} />
					{lead ? (
						<LeftColumn
							lead={lead}
							sessionDialedNumbers={sessionDialed}
							onAddContact={handleOpenAddContact}
							onEditContact={handleOpenEditContact}
							onToggleDecisionMaker={handleToggleDecisionMaker}
							onDeleteContact={handleDeleteContact}
							onCallContact={handleCallContact}
							onComposeEmail={handleComposeEmail}
							onTextContact={handleTextContact}
							textState={repTexting.state}
							unreadTextCountByE164={unreadTextCountByE164}
							onFindCellPhone={handleFindCellPhone}
							findCellPhonePendingId={
								findCellPhoneMutation.isPending ? (findCellPhoneMutation.variables ?? null) : null
							}
							// Disable Call affordances while the coaching gate waits, matching
							// legacy `CallButton`'s `isStartingCoaching` disabled state.
							callPending={dialPending}
							onEditCompany={() => setEditCompanyOpen(true)}
							onCallCompanyPhone={handleCallCompanyPhone}
							onManageCompanyPhones={() => setCompanyPhonesOpen(true)}
							enableSpar={isCoffeeAccount}
							sparDisabled={callIsActive || sparSession.isConnecting}
							onCallSpar={handleSparContact}
						/>
					) : queueEmpty ? (
						<EmptyQueueState
							onTryAgain={loadNextLead}
							onBackToQueue={handleExit}
							loading={nextLeadMutation.isPending}
						/>
					) : null}
				</div>
				<div className={css(styles.colMid)}>
					<TopBar
						onNextLead={handleNextLead}
						onClose={handleExit}
						onSkipLead={currentCompanyId ? () => handleOpenSkipDialog('skip') : undefined}
						onRemoveLead={currentCompanyId ? () => handleOpenSkipDialog('remove') : undefined}
						onReportIssue={currentCompanyId ? () => setReportIssueOpen(true) : undefined}
						skipLeadDisabled={!currentCompanyId || skipLeadMutation.isPending}
						nav={
							<>
								<FocusedNav
									onSearch={() => appDrawerContext.setValue(DRAWER_VALUE_SEARCH)}
									canNavigate={guardLeavingView}
								/>
								{repTexting.state === 'ready' ? (
									<TextInboxMenu
										items={textInbox.items}
										status={
											unreadTextQuery.isPending
												? 'loading'
												: unreadTextQuery.isError && !unreadTextConversations
													? 'error'
													: 'ready'
										}
										onOpen={handleOpenTextFromInbox}
									/>
								) : null}
							</>
						}
					/>
					<CenterColumn
						callState={callState}
						muted={muted}
						onAnswer={accept}
						onReject={reject}
						onHangup={hangup}
						onToggleMute={() => setMuted(!muted)}
						onEndPractice={sparSession.end}
						onEnableAudio={handleEnableAudio}
						onSendDigit={sendDigits}
						peerDisplay={peerDisplay}
						fromNumber={baseLead?.suggestedFromNumberE164 ?? userSession.telephonyConfiguration?.phoneNumber?.e164}
						inboundDisplay={ringingInboundDisplay}
						onOpenInboundLead={
							ringingInboundCompanyId
								? () => {
										if (ringingInboundCompanyId !== currentCompanyId) {
											setCurrentCompanyId(ringingInboundCompanyId);
											setQueueEmpty(false);
										}
									}
								: undefined
						}
						reconnecting={reconnecting}
						messages={displayMessages}
						pinnedIds={displayPinnedIds}
						onTogglePin={togglePin}
						isResponding={isResponding}
						starters={keywordPins.filter(p => p.trigger === 'starter' && p.enabled)}
						onSendStarter={handleSendStarter}
						wsConnected={isWsConnected}
						showGetSuggestionButton={!autoSuggestionsEnabled}
						onRequestSuggestion={requestSuggestion}
						showGetSummaryButton={callEndedPendingSummary}
						onRequestSummary={handleRequestSummary}
						coachTemplateName={coaching.agentTemplateName ?? undefined}
						waitingForGamePlan={coaching.isWaitingForGamePlan}
						showLiveTranscript={showLiveTranscript}
					/>
				</div>

				<SideDrawer styles={[styles.drawer]}>
					<AppDrawerContent value={DRAWER_VALUE_SEARCH} styles={[styles.drawerContent]} unmountOnExit>
						<SearchPanel
							onResultSelected={pickedCompanyId => {
								// Sync state directly. HashHistory silently rejects pushing the
								// same path twice, so we can't rely on the URL effect to fire
								// when the rep picks the lead they were just on.
								if (pickedCompanyId !== currentCompanyId) {
									setCurrentCompanyId(pickedCompanyId);
								}
								setQueueEmpty(false);
								appDrawerContext.setValue('');
							}}
							rescueMode={!!unknownInboundCall}
							rescuePhone={unknownInboundCall?.from}
							initialQuery={unknownInboundCall?.from ?? ''}
							onRescueAttach={handleRescueAttach}
						/>
					</AppDrawerContent>
					<AppDrawerContent value={DRAWER_VALUE_AI} styles={[styles.drawerContent]}>
						<AIChatPanel
							messages={displayMessages}
							pinnedIds={displayPinnedIds}
							onTogglePin={togglePin}
							isResponding={isResponding}
							onSend={handleSend}
							onNewConversation={() => {
								clearPinned();
								startNewConversation();
							}}
						/>
					</AppDrawerContent>
					<AppDrawerContent value={DRAWER_VALUE_FEEDBACK} styles={[styles.drawerContent]}>
						<RecentCallsFeedbackPanel
							messages={recentCallsFeedback.messages}
							phase={recentCallsFeedback.phase}
							isResponding={recentCallsFeedback.isResponding}
							followUpFailed={recentCallsFeedback.followUpFailed}
							onStart={() => void recentCallsFeedback.start()}
							onSend={recentCallsFeedback.send}
						/>
					</AppDrawerContent>
					<AppDrawerContent value={DRAWER_VALUE_PAST_CALLS} styles={[styles.drawerContent]}>
						<PastCallsPanel
							companyId={currentCompanyId}
							contacts={lead?.contacts}
							onSendToAI={handleSendPastTranscript}
						/>
					</AppDrawerContent>
					<AppDrawerContent value={DRAWER_VALUE_WRAPUP} styles={[styles.drawerContent]} unmountOnExit>
						<WrapupPanel
							companyId={currentCompanyId}
							phoneCallId={activeCall?.phoneCallId ?? lastEndedPhoneCallId ?? 'manual'}
							source={queryLeadServedSource}
							leadName={lead?.company}
							contactName={primaryContact?.name}
							leadTimeZone={lead?.rawCompany?.timeZone}
							lastDealId={lead?.lastDealId}
							onSubmitted={handleWrapupSubmitted}
						/>
					</AppDrawerContent>
					<AppDrawerContent value={DRAWER_VALUE_QUICK_ACTIONS} styles={[styles.drawerContent]} unmountOnExit>
						<QuickActionsPanel
							companyId={currentCompanyId}
							source={queryLeadServedSource}
							leadName={lead?.company}
							contactName={primaryContact?.name}
							leadTimeZone={lead?.rawCompany?.timeZone}
							lastDealId={lead?.lastDealId}
							onActionComplete={() => appDrawerContext.setValue('')}
						/>
					</AppDrawerContent>
					{!pinsLocked && (
						<AppDrawerContent value={DRAWER_VALUE_PIN_CONFIG} styles={[styles.drawerContent]} unmountOnExit>
							<PinConfigPanel
								pins={ownKeywordPins}
								onToggleEnabled={toggleEnabled}
								onDelete={remove}
								onEdit={pin => setEditing(pin)}
								onNew={() => setEditing('new')}
							/>
						</AppDrawerContent>
					)}
					{isCoffeeAccount && (
						<AppDrawerContent value={DRAWER_VALUE_SETTINGS} styles={[styles.drawerContent]} unmountOnExit>
							<SettingsPanel
								realtimeCoachingEnabled={coachingPrefs.realtimeCoachingEnabled}
								autoSuggestionsEnabled={coachingPrefs.autoSuggestionsEnabled}
								focusedViewEnabled={focusedViewEnabled}
								focusedViewLocked={focusedViewLocked}
								showLiveTranscript={showLiveTranscript}
								onShowLiveTranscriptChange={setShowLiveTranscript}
								onRealtimeChange={enabled => void coachingPrefs.setRealtimeCoaching(enabled)}
								onAutoSuggestionsChange={enabled => void coachingPrefs.setAutoSuggestions(enabled)}
								onReturnToQueue={handleReturnToQueue}
							/>
						</AppDrawerContent>
					)}
					{isCoffeeAccount && (
						<AppDrawerContent value={DRAWER_VALUE_PRACTICE} styles={[styles.drawerContent]} unmountOnExit>
							<PracticeModePanel
								isActive={practiceMode.isActive}
								onEnter={handleEnterPractice}
								onExit={handleExitPractice}
								scenarios={practiceScenarios}
								skillAreas={practiceSkillAreas}
								categories={practiceCategories}
								selectedScenarioId={selectedScenarioId}
								onSelectScenario={setSelectedScenarioId}
								isLoadingLead={nextLeadMutation.isPending}
								sparTarget={
									sparTargetContact
										? {
												name:
													sparTargetContact.name ||
													[sparTargetContact.firstName, sparTargetContact.lastName].filter(Boolean).join(' ') ||
													'this contact',
											}
										: null
								}
								onStartSpar={handleStartSpar}
								isSparConnecting={sparSession.isConnecting}
							/>
						</AppDrawerContent>
					)}

					<div className={css(styles.toolbarOverride)}>
						<CoffeeDrawerToolbar>
							<AppDrawerToolbarButton value={DRAWER_VALUE_SEARCH} tooltip='Search'>
								<IconSearch size={15} color='currentColor' />
							</AppDrawerToolbarButton>
							<AppDrawerToolbarButton value={DRAWER_VALUE_AI} tooltip='AI Conversation'>
								<IconMessageSquare size={15} sw={2} color='currentColor' />
								<CountBadge count={unreadCount} aria-label={`${unreadCount} unread`} />
							</AppDrawerToolbarButton>
							<AppDrawerToolbarButton value={DRAWER_VALUE_FEEDBACK} tooltip='Coaching feedback'>
								<IconSparkles size={15} sw={2} color='currentColor' />
							</AppDrawerToolbarButton>
							{/* "AI Conversations" (past-convos) panel removed at product request — it
							    only ever showed seeded placeholder data. Restore from git history if asked. */}
							<AppDrawerToolbarButton value={DRAWER_VALUE_PAST_CALLS} tooltip='Past Calls'>
								<IconFileText size={15} color='currentColor' />
							</AppDrawerToolbarButton>
							<AppDrawerToolbarButton value={DRAWER_VALUE_WRAPUP} tooltip='Wrap-up'>
								<IconCalendarNote size={15} color='currentColor' />
							</AppDrawerToolbarButton>
							<AppDrawerToolbarButton value={DRAWER_VALUE_QUICK_ACTIONS} tooltip='Quick Actions'>
								<IconPlus size={15} color='currentColor' />
							</AppDrawerToolbarButton>
							{!pinsLocked && (
								<AppDrawerToolbarButton value={DRAWER_VALUE_PIN_CONFIG} tooltip='Pin Config'>
									<IconPin size={15} color='currentColor' />
								</AppDrawerToolbarButton>
							)}
							{isCoffeeAccount && (
								<AppDrawerToolbarButton value={DRAWER_VALUE_PRACTICE} tooltip='Practice Mode'>
									<IconMic size={15} color='currentColor' />
								</AppDrawerToolbarButton>
							)}
							{isCoffeeAccount && (
								<AppDrawerToolbarButton value={DRAWER_VALUE_SETTINGS} tooltip='Settings'>
									<SettingsIcon stroke='currentColor' />
								</AppDrawerToolbarButton>
							)}
						</CoffeeDrawerToolbar>
					</div>
				</SideDrawer>
			</div>

			{!pinsLocked && editing !== null && (
				<PinEditor
					initial={editing === 'new' ? null : editing}
					onCancel={() => setEditing(null)}
					onSave={handleSavePin}
				/>
			)}

			<EnableAudioDialog
				open={audioDialogOpen}
				onOpenChange={handleAudioDialogOpenChange}
				onEnable={handleEnableFromDialog}
			/>

			<PacingMilestoneDialog
				open={milestoneTrigger.open}
				onOpenChange={next => (next ? undefined : milestoneTrigger.close())}
				milestone={milestoneTrigger.milestone}
				onGetFeedback={handleGetFeedback}
				adherence={adherence.data}
			/>

			<EditCompanyDialog
				open={editCompanyOpen}
				onOpenChange={setEditCompanyOpen}
				lead={lead}
				onSave={handleSaveCompany}
				saving={updateCompanyMutation.isPending}
			/>

			<ReportIssueDialog
				open={reportIssueOpen}
				onOpenChange={setReportIssueOpen}
				companyId={currentCompanyId}
				companyName={lead?.company}
			/>

			<ComposeEmailDialog
				open={composeContact !== null}
				onOpenChange={open => {
					if (!open) setComposeContact(null);
				}}
				companyId={currentCompanyId}
				contact={composeContact ?? undefined}
			/>

			<TextingBlockedDialog open={textingBlockedOpen} onOpenChange={setTextingBlockedOpen} />

			<TextConversationDialog
				open={textTarget !== null}
				onOpenChange={open => {
					if (!open) setTextTarget(null);
				}}
				contact={textTarget?.contact}
				phone={textTarget?.phone}
				companyName={
					textTarget?.contact && lead?.contacts.some(contact => contact.id === textTarget.contact?.id)
						? lead.company || undefined
						: undefined
				}
				textingNumber={repTexting.textingNumber}
			/>

			<CompanyPhonesDialog
				open={companyPhonesOpen}
				onOpenChange={setCompanyPhonesOpen}
				initial={baseLead?.rawCompany?.phoneNumbers ?? []}
				onSave={handleSaveCompanyPhones}
				saving={updateCompanyMutation.isPending}
			/>

			<ContactDialog
				open={contactDialogState !== null}
				mode={contactDialogState?.mode ?? 'add'}
				onOpenChange={handleCloseContactDialog}
				contact={editingContact}
				onSave={handleSaveContact}
				saving={addContactMutation.isPending || updateContactMutation.isPending}
			/>

			<CellPhonePickerDialog
				open={cellPhonePickerContact !== null}
				onOpenChange={handleCloseCellPhonePicker}
				contactName={cellPhonePickerContact?.name ?? ''}
				result={cellPhoneResult ?? undefined}
				loading={findCellPhoneMutation.isPending}
				error={cellPhoneError}
				onRetry={handleRetryCellPhoneLookup}
				onCall={handleCallCellPhoneNumber}
				onAddNumber={handleAddCellPhoneNumber}
				onMarkWrong={handleMarkWrongNumber}
				addedNumbers={cellPhoneAdded}
				pendingNumber={cellPhoneAddingNumber}
				markWrongPendingNumber={cellPhoneMarkWrongPending}
				callPending={dialPending}
			/>

			<SkipReasonDialog
				open={skipDialogOpen}
				onOpenChange={open => {
					// Block dismissal while a skip is in flight — covers both the
					// note-save and skip phases (pendingSkipReasonId spans both).
					if (!open && pendingSkipReasonId !== null) return;
					// Drop any cached note when the dialog closes so a fresh session
					// starts clean.
					if (!open) skipNoteCacheRef.current = null;
					setSkipDialogOpen(open);
				}}
				skipReasons={skipReasonsResult.skipReasons}
				tempSkipReasons={skipReasonsResult.tempSkipReasons}
				loading={skipReasonsResult.isLoading}
				error={skipReasonsResult.error?.message}
				pendingReasonId={pendingSkipReasonId}
				onPick={handlePickSkipReason}
				mode={skipDialogMode}
				onContinueWithoutReason={
					skipDialogMode === undefined
						? () => {
								setSkipDialogOpen(false);
								loadNextLead();
							}
						: undefined
				}
			/>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		height: '100vh',
		width: '100vw',
		overflow: 'hidden',
	},
	focusedViewRoot: {
		background: 'var(--bg)',
		color: 'var(--ink)',
	},
	app: {
		display: 'grid',
		// Fallback only — the left track width is driven by an inline style
		// (persisted, resizable) on the container. See LEFT_COL_WIDTH_KEY.
		gridTemplateColumns: '340px minmax(0, 1fr) auto',
		height: '100vh',
		width: '100vw',
	},
	colLeft: {
		position: 'relative',
		minWidth: 0,
		minHeight: 0,
		height: '100vh',
		borderRight: '1px solid var(--border)',
		background: 'var(--bg)',
	},
	colMid: {
		minWidth: 0,
		minHeight: 0,
		height: '100vh',
		background: 'var(--bg)',
		display: 'flex',
		flexDirection: 'column',
	},
	drawer: {
		height: '100vh',
	},
	drawerContent: {
		height: '100vh',
	},
	// Local override for the focused-view drawer toolbar. components-v2's
	// default `drawerButton` style paints inactive buttons with
	// `color: primary.main` on `primary.light`, which reads as disabled in
	// this surface. Override only the inactive (`data-state="off"`) state —
	// the active (`data-state="on"`) styling, including the left rail
	// indicator pseudo-element, is intentionally untouched.
	// `:nth-child(1n)` bumps specificity above the components-v2 default
	// without modifying `Drawer/styles.ts` (which is shared by every
	// consumer). Descendant selector reaches the button regardless of the
	// Tooltip `<span>` wrapper.
	toolbarOverride: {
		':nth-child(1n) [data-state="off"]': {
			color: 'var(--ink)',
			backgroundColor: 'transparent',
			border: '1px solid var(--border)',
		},
		':nth-child(1n) [data-state="off"]:hover': {
			backgroundColor: 'var(--hover, rgba(0, 0, 0, 0.04))',
		},
	},
});
